import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useUiStore } from '@/store';
import { useTenantId } from './useTenant';

// V-222. Keyed on `message`, not `id`. A live push gets a synthetic
// `live-<timestamp>` id (below); once the refetch this file's own dedup
// comment describes catches up, the live entry is dropped in favour of
// the persisted row carrying the SAME message under a REAL event id. An
// id-keyed "seen" marker stored while the live entry was still
// notifications[0] can never match that real id, and unreadCount's own
// fallback then counts everything unread again — resetting the exact
// notification the admin just opened the bell to read. `message` is
// already this file's own notion of "the same notification" (see the
// dedup below), so tracking "seen" the same way survives the swap.

/**
 * The header bell.
 *
 * The backend reconstructs persisted alerts into the same `{message,
 * severity, created_at}` shape the live SSE pushes carry, so there is one
 * rendering path whether a notification was fetched on load or arrived while
 * the page was open. Live pushes are prepended locally rather than triggering
 * a refetch — the payload already contains everything the row needs.
 *
 * "Unread" is tracked by the MESSAGE of the newest notification the admin
 * has seen (not its id — see V-222 below), persisted across reloads. A
 * count that resets every refresh trains people to ignore the badge.
 *
 * lastSeenMessage lives in the shared uiStore (V-309), not a local
 * useState here: NotificationBell, BottomNav and AlertsView each call this
 * hook from their own, independently mounted component, and only a value
 * genuinely shared across all three keeps them agreeing about what has
 * been read.
 */
export function useNotifications() {
  const tenantId = useTenantId();
  const lastEvent = useUiStore((s) => s.lastEvent);
  const lastSeenMessage = useUiStore((s) => s.lastSeenNotification);
  const setLastSeenNotification = useUiStore((s) => s.setLastSeenNotification);
  const [live, setLive] = useState([]);

  const { data: fetched = [] } = useQuery({
    queryKey: qk.notifications(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/notifications`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  // Only these reasons are bell-worthy. An ordinary defect report or a
  // routine status change is already visible in the view that owns it, and
  // putting everything here would make the bell noise rather than signal.
  useEffect(() => {
    if (!lastEvent?.reason) return;
    const { reason, message, license_plate: plate, severity, status } = lastEvent;

    let entry = null;
    if (reason === 'vehicle_compliance_critical') entry = { severity: 'critical' };
    else if (reason === 'vehicle_compliance_warning') entry = { severity: 'warning' };
    // The driver half of the same engine. Left out, a 95 code running out in
    // five days would reach the tenant's Telegram chat and never appear on
    // the screen the dispatcher actually has open.
    else if (reason === 'driver_compliance_critical') entry = { severity: 'critical' };
    else if (reason === 'driver_compliance_warning') entry = { severity: 'warning' };
    else if (reason === 'dwell_exceeded') entry = { severity: 'critical' };
    // V-11's maintenance sweep. Left out, a truck going overdue would reach
    // the tenant's Telegram chat and never appear on the screen the
    // dispatcher actually has open — the identical gap the driver-compliance
    // comment above describes, and the engine publishes this reason only on
    // a run that actually announced something.
    else if (reason === 'service_digest') {
      entry = { severity: lastEvent.overdue ? 'critical' : 'warning' };
    }
    // V-9's unbilled sweep, the money twin of the maintenance one above. The
    // engine publishes this reason only on a run it actually sent, and app.py
    // replaces the payload's Telegram text with the same one-line sentence
    // /notifications builds from the persisted row — so the live push and the
    // refetch that follows it collapse into one entry below.
    //
    // Always a warning: nothing here is past a deadline. Delivered work that
    // has not been invoiced has no due date to breach, and colouring it the
    // same as a grounded truck would cost the colour its meaning.
    else if (reason === 'unbilled_digest') entry = { severity: 'warning' };
    // `dwell_unclosed` is deliberately NOT here either, and its own site
    // says why: app.py's run_dwell_watch_for_tenant() records that "the
    // escalation that matters is the dispatcher panel and this timeline
    // entry, both of which are durable". A stop nobody closed is a
    // measurement that stopped being a measurement — worth a durable row,
    // not worth interrupting the person who would have to close it.
    //
    // `receivables_reminder` is deliberately NOT here. Its persisted rows are
    // one per overdue invoice — number, client and amount, which is what the
    // owner quotes on the phone — while its SSE payload carries only totals
    // and a list of ids. A live summary line would therefore be a SECOND,
    // differently-shaped copy of the same debt, which the message-equality
    // rule below cannot collapse. The itemised rows arrive on the next
    // refetch, and an invoice thirty days late does not become urgent in the
    // sixty seconds that costs.
    // The four live GPS findings. All of them are pushed by the code that
    // writes the system_events row, with the SAME `message` the bell's own
    // refetch will render from that row — so the message-equality rule
    // below collapses the live push and the fetched copy into one entry
    // instead of showing each alert twice. (That equality is exactly what
    // `receivables_reminder` above cannot satisfy, which is why it is not
    // here and these are.)
    //
    // A loaded truck sustained outside its route corridor is critical, with
    // the same reasoning `dwell_exceeded` is: it is happening now, and the
    // window in which a dispatcher can do anything about it closes.
    else if (reason === 'route_deviation') entry = { severity: 'critical' };
    // V-382. The fourth, and it was the one the count above forgot. Its
    // publisher's own comment claimed this file already treated it as
    // bell-live and toast-interrupting; this file had never heard of it,
    // so a truck sitting at a competitor's quarry reached Telegram and the
    // fleet-integrity screen and never the dispatcher's bell.
    //
    // Critical, and not by a judgement made here: blueprints/reporting.py
    // already renders the persisted row as critical, and does it in these
    // words — "both name a truck that is somewhere it has no business
    // being, while the load is still on it" — grouping it with
    // route_deviation above.
    else if (reason === 'gps_diversion_alert') entry = { severity: 'critical' };
    // A truck that has gone dark, and a tracker sending impossible fixes,
    // are both warnings: they say this system has stopped being able to see
    // something, which is worth knowing and is not, by itself, evidence of
    // anything. See FLEET_INTEGRITY_CATEGORY_WEIGHTS for the same call made
    // about the same two findings on the risk score.
    else if (reason === 'gps_signal_lost') entry = { severity: 'warning' };
    else if (reason === 'gps_jump_pattern') entry = { severity: 'warning' };
    // The two anomaly sweeps that only reached this bell by refetch until
    // they learned to push (app.py's run_odometer_check_for_tenant() /
    // run_fuel_anomaly_check_for_tenant()). Warnings, not critical: both say
    // a truck's numbers do not add up, and both have an innocent
    // explanation at least as often as a guilty one.
    else if (reason === 'odometer_mismatch') entry = { severity: 'warning' };
    else if (reason === 'fuel_anomaly') entry = { severity: 'warning' };
    else if (reason === 'vehicle_defect_reported' && severity === 'critical_grounded') entry = { severity: 'critical' };
    else if (reason === 'vehicle_defect_updated' && status === 'resolved') entry = { severity: 'resolved' };
    if (!entry) return;

    setLive((prev) => [
      {
        id: `live-${lastEvent.at}`,
        message: message || plate || reason,
        severity: entry.severity,
        created_at: new Date(lastEvent.at).toISOString(),
      },
      ...prev,
    ]);
  }, [lastEvent]);

  // Live pushes that the refetch has since caught up with are dropped, keyed
  // on the message itself. Both feeds describe the same events — an SSE push
  // and, within `staleTime`, the persisted row rebuilt into the same shape —
  // so without this the bell showed every alert twice, once live and once
  // fetched, from the day the live path was added.
  //
  // The message is a sound key because these sentences name their subject: a
  // plate, an invoice number, a count. Two events that produce byte-identical
  // text are saying the same thing about the same subject, and the second
  // copy is noise whether or not a different event id sits behind it.
  // `resolvable`: whether this row can be sent to POST .../resolve. A live
  // push carries a synthetic `live-<timestamp>` id (see setLive() above) —
  // nothing on the server for that id, so a resolve click on one would 404.
  // A FETCHED row's id is always a real system_events.event_id, and
  // resolve_system_event() places no restriction on which event_type it
  // accepts — see its own docstring ("the dispatcher's 'I looked at this,
  // it's handled' action") — so every persisted notification qualifies.
  const notifications = useMemo(() => {
    const already = new Set(fetched.map((n) => n.message));
    return [
      ...live.filter((n) => !already.has(n.message)).map((n) => ({ ...n, resolvable: false })),
      ...fetched.map((n) => ({ ...n, resolvable: true })),
    ].slice(0, 40);
  }, [live, fetched]);

  const unreadCount = useMemo(() => {
    if (!lastSeenMessage) return notifications.length;
    const index = notifications.findIndex((n) => n.message === lastSeenMessage);
    return index === -1 ? notifications.length : index;
  }, [notifications, lastSeenMessage]);

  const markSeen = () => {
    const newest = notifications[0]?.message;
    if (!newest) return;
    setLastSeenNotification(newest);
  };

  return { notifications, unreadCount, markSeen };
}

/**
 * POST /api/tenants/<id>/system-events/<eventId>/resolve — the dispatcher's
 * "I looked at this, it's handled" action on one bell row (see
 * blueprints/reporting.py's resolve_system_event() docstring). Only ever
 * called with a `resolvable` notification's real event id — see
 * useNotifications()'s own comment for why a live-pushed row can't be
 * resolved. Invalidates the bell's own query: resolving drops the row from
 * list_notifications()'s next fetch (resolved_at IS NOT NULL), so the panel
 * should refresh rather than keep showing something already handled.
 */
export function useResolveNotification() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId) => api(`/api/tenants/${tenantId}/system-events/${eventId}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.notifications(tenantId) });
      // resolve_system_event() moves fleet_integrity()'s counts/risk_score
      // too (see its own docstring), and both queries below carry a 60s
      // staleTime — without this a dispatcher who resolves a finding and
      // then opens either modal would see the pre-resolve numbers for up
      // to a minute.
      queryClient.invalidateQueries({ queryKey: ['fleet-integrity', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['client-reliability', tenantId] });
      // V-372. The three above are the surfaces resolve_system_event()'s own
      // docstring names. There is a fourth it does not: an unresolved
      // finding is core/invoicing.py's billing hold, and V-335 records that
      // pressing resolve IS the documented way to release a delivered,
      // weighed, POD'd load that will not invoice. Without these the
      // dispatcher who pressed it goes to Billing and the load is still
      // listed as excluded.
      ['unbilled', 'invoice-draft', 'worklist'].forEach((prefix) => {
        queryClient.invalidateQueries({ queryKey: [prefix, tenantId] });
      });
    },
  });
}

/**
 * Toast for the two alerts that must interrupt whatever tab is open: a
 * critical compliance deadline and a truck sitting over its dwell allowance.
 * Everything else goes to the bell only.
 */
export function useCriticalToast() {
  const lastEvent = useUiStore((s) => s.lastEvent);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!lastEvent?.reason) return undefined;
    const interrupting =
      lastEvent.reason === 'vehicle_compliance_critical' ||
      lastEvent.reason === 'driver_compliance_critical' ||
      lastEvent.reason === 'dwell_exceeded' ||
      // Interrupting for the same reason dwell_exceeded is, and only these
      // two of the four GPS findings: a loaded truck leaving its route, or
      // sitting at a competitor's quarry, is happening right now and stops
      // being actionable if it waits for the dispatcher to look at the
      // bell. A dark tracker and a run of impossible fixes go to the bell
      // alone — they are worth telling somebody, not worth taking over
      // their screen.
      //
      // V-382: the second of the two is new here. The criterion is the
      // backend's own, not one invented in this file — reporting.py groups
      // the two together as naming "a truck that is somewhere it has no
      // business being, while the load is still on it".
      lastEvent.reason === 'route_deviation' ||
      lastEvent.reason === 'gps_diversion_alert' ||
      (lastEvent.reason === 'vehicle_defect_reported' && lastEvent.severity === 'critical_grounded');
    if (!interrupting) return undefined;

    setToast({
      // full_name for the driver half; license_plate stays the fallback for
      // the vehicle half. Neither is ever the message itself — that is what
      // `message` carries — but a payload that lost it must still say WHO.
      message: lastEvent.message || lastEvent.full_name || lastEvent.license_plate || lastEvent.reason,
      targetView:
        // route_deviation and gps_diversion_alert land on the dispatcher
        // board with dwell_exceeded: all three are about a load in flight,
        // and the map is where a dispatcher can see where the truck
        // actually is.
        lastEvent.reason === 'dwell_exceeded' || lastEvent.reason === 'route_deviation'
        || lastEvent.reason === 'gps_diversion_alert'
          ? 'dispatcher'
          : lastEvent.reason === 'driver_compliance_critical'
            ? 'drivers'
            : 'fleet',
      at: lastEvent.at,
    });
    const timer = window.setTimeout(() => setToast(null), 12_000);
    return () => window.clearTimeout(timer);
  }, [lastEvent]);

  return { toast, dismiss: () => setToast(null) };
}
